import React, { useState, useEffect } from 'react';
import styles from './PersonaDetail.module.css';
import { personasAPI } from '../../services/api';

interface BehavioralDeterminant {
  description?: string;
  effect?: string;
  leverage?: string;
  source?: string;
}

interface Top3Determinant {
  name?: string;
  reasoning?: string;
  communicationImplication?: string;
}

interface PersonaData {
  _id: string;
  name: string;
  type: 'primary' | 'secondary' | 'tertiary';
  avatar?: string;
  demographics?: {
    age?: { min?: number; max?: number };
    ageRange?: string;
    gender?: string;
    education?: string;
    occupation?: string;
    income?: string;
    location?: { city?: string; region?: string; country?: string } | string;
    familyStatus?: string;
  };
  psychographics?: {
    interests?: string[];
    values?: string[];
    lifestyle?: string;
    personality?: string;
    motivations?: string[];
    frustrations?: string[];
    goals?: string[];
    fears?: string[];
  };
  behaviors?: {
    mediaConsumption?: {
      preferredChannels?: string[];
      onlineHours?: number;
      devices?: string[];
    } | string[];
    buyingBehavior?: string;
    communicationPreferences?: string[];
  };
  projectRelation?: {
    awareness?: string;
    awarenessLevel?: string;
    sentiment?: string;
    influence?: string;
    engagement?: string;
  };
  needs?: string[];
  painPoints?: string[];
  quotes?: string[];
  notes?: string;
  // CASI fields
  biography?: string;
  quote?: string;
  dailyRoutine?: string[];
  currentBehavior?: string;
  desiredBehavior?: string;
  triggers?: string[];
  momentOfTruth?: string;
  behavioralDeterminants?: Record<string, BehavioralDeterminant>;
  top3Determinants?: Top3Determinant[];
  interventionChecklist?: string[];
  qualityControl?: {
    stereotypeRisks?: string[];
    actionableQuestions?: string[];
  };
  sourcesAndDataQuality?: {
    projectSources?: string;
    assumptions?: string;
    knowledgeGaps?: string;
  };
  communicationPreferences?: string[];
}

interface PersonaDetailProps {
  persona: PersonaData;
  onClose: () => void;
  canEdit?: boolean;
  onUpdate?: (updated: PersonaData) => void;
}

const typeLabels: Record<string, string> = {
  primary: 'Primair',
  secondary: 'Secundair',
  tertiary: 'Tertiair'
};

const typeDescriptions: Record<string, string> = {
  primary: 'Direct geraakt — woont, werkt of verblijft in het invloedsgebied',
  secondary: 'Indirect geraakt — ondervindt gevolgen via omleiding, waardedaling of veranderd voorzieningenniveau',
  tertiary: 'Observerend/beleidsmatig — volgt het project vanuit bestuurlijke, politieke of maatschappelijke rol'
};

const determinantLabels: Record<string, string> = {
  weerstand: 'Weerstand',
  zelfbeeld: 'Zelfbeeld',
  automatismen: 'Automatismen',
  emoties: 'Emoties',
  socialeOmgeving: 'Sociale Omgeving',
  fysiekeOmgeving: 'Fysieke Omgeving',
  kunnen: 'Kunnen',
  kennis: 'Kennis',
  houdingEnRisicoperceptie: 'Houding & Risicoperceptie'
};

const familyStatusLabels: Record<string, string> = {
  single: 'Alleenstaand',
  married: 'Getrouwd',
  divorced: 'Gescheiden',
  widowed: 'Weduwe/Weduwnaar',
  partnered: 'Samenwonend'
};

const awarenessLabels: Record<string, string> = {
  unaware: 'Onbewust',
  aware: 'Bewust',
  interested: 'Geïnteresseerd',
  engaged: 'Betrokken',
  advocate: 'Ambassadeur'
};

const sentimentLabels: Record<string, string> = {
  very_negative: 'Zeer negatief',
  negative: 'Negatief',
  neutral: 'Neutraal',
  positive: 'Positief',
  very_positive: 'Zeer positief'
};

const influenceLabels: Record<string, string> = {
  low: 'Laag',
  medium: 'Gemiddeld',
  high: 'Hoog',
  very_high: 'Zeer hoog'
};

const engagementLabels: Record<string, string> = {
  passive: 'Passief',
  active: 'Actief',
  very_active: 'Zeer actief'
};

// Editable text field
const EditableText: React.FC<{
  value: string;
  onChange: (val: string) => void;
  editing: boolean;
  multiline?: boolean;
  placeholder?: string;
  className?: string;
}> = ({ value, onChange, editing, multiline, placeholder, className }) => {
  if (!editing) {
    return <span className={className}>{value || <span className={styles.emptyText}>{placeholder || 'Niet ingevuld'}</span>}</span>;
  }
  if (multiline) {
    return (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={styles.editTextarea}
        placeholder={placeholder}
        rows={3}
      />
    );
  }
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={styles.editInput}
      placeholder={placeholder}
    />
  );
};

// Editable tags (array of strings)
const EditableTags: React.FC<{
  values: string[];
  onChange: (vals: string[]) => void;
  editing: boolean;
  placeholder?: string;
}> = ({ values, onChange, editing, placeholder }) => {
  if (!editing) {
    if (!values || values.length === 0) return <span className={styles.emptyText}>Niet ingevuld</span>;
    return (
      <div className={styles.tagList}>
        {values.map((v, i) => (
          <span key={i} className={styles.tagItem}>{v}</span>
        ))}
      </div>
    );
  }
  return (
    <input
      type="text"
      value={values?.join(', ') || ''}
      onChange={(e) => onChange(e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
      className={styles.editInput}
      placeholder={placeholder || 'Gescheiden door komma\'s'}
    />
  );
};

// Editable select
const EditableSelect: React.FC<{
  value: string;
  onChange: (val: string) => void;
  editing: boolean;
  options: { value: string; label: string }[];
  labels?: Record<string, string>;
  className?: string;
}> = ({ value, onChange, editing, options, labels, className }) => {
  if (!editing) {
    const displayVal = labels?.[value] || value;
    return <span className={className}>{displayVal || <span className={styles.emptyText}>Niet ingevuld</span>}</span>;
  }
  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className={styles.editSelect}
    >
      <option value="">-- Selecteer --</option>
      {options.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
};

export const PersonaDetail: React.FC<PersonaDetailProps> = ({ persona: initialPersona, onClose, canEdit = true, onUpdate }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'behavior' | 'intervention'>('overview');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [persona, setPersona] = useState<PersonaData>(initialPersona);
  const [editData, setEditData] = useState<PersonaData>(initialPersona);

  useEffect(() => {
    setPersona(initialPersona);
    if (!editing) {
      setEditData(initialPersona);
    }
  }, [initialPersona, editing]);

  const startEditing = () => {
    setEditData(JSON.parse(JSON.stringify(persona)));
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditData(JSON.parse(JSON.stringify(persona)));
    setEditing(false);
  };

  const saveChanges = async () => {
    setSaving(true);
    try {
      const dataToSend: any = { ...editData };
      delete dataToSend._id;
      delete dataToSend.__v;
      delete dataToSend.createdAt;
      delete dataToSend.updatedAt;

      await personasAPI.update(persona._id, dataToSend);
      setPersona(editData);
      setEditing(false);
      if (onUpdate) {
        onUpdate(editData);
      }
    } catch (error) {
      console.error('Failed to save persona:', error);
    } finally {
      setSaving(false);
    }
  };

  const p = editing ? editData : persona;
  const hasCASIData = p.behavioralDeterminants && Object.keys(p.behavioralDeterminants).length > 0;

  const updateField = (path: string, value: any) => {
    setEditData(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      const parts = path.split('.');
      let obj = updated;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!obj[parts[i]]) obj[parts[i]] = {};
        obj = obj[parts[i]];
      }
      obj[parts[parts.length - 1]] = value;
      return updated;
    });
  };

  const getLocationString = () => {
    if (!p.demographics?.location) return '';
    if (typeof p.demographics.location === 'string') return p.demographics.location;
    const loc = p.demographics.location;
    return [loc.city, loc.region, loc.country].filter(Boolean).join(', ');
  };

  const getMediaChannels = () => {
    if (!p.behaviors?.mediaConsumption) return [];
    if (Array.isArray(p.behaviors.mediaConsumption)) return p.behaviors.mediaConsumption;
    return p.behaviors.mediaConsumption.preferredChannels || [];
  };

  return (
      <div className={styles.personaDetail}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            {p.avatar ? (
              <div className={styles.headerAvatar}>
                <img src={p.avatar} alt={p.name} />
              </div>
            ) : (
              <div className={styles.headerAvatarFallback}>
                {p.name?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
              </div>
            )}
            <div className={styles.headerInfo}>
              {editing ? (
                <input
                  type="text"
                  value={editData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  className={styles.editInputHeader}
                  placeholder="Naam"
                />
              ) : (
                <h1>{p.name}</h1>
              )}
              <div className={styles.headerMeta}>
                {editing ? (
                  <select
                    value={editData.type}
                    onChange={(e) => updateField('type', e.target.value)}
                    className={styles.editSelectHeader}
                  >
                    <option value="primary">Primair — direct geraakt</option>
                    <option value="secondary">Secundair — indirect geraakt</option>
                    <option value="tertiary">Tertiair — observerend/beleidsmatig</option>
                  </select>
                ) : (
                  <span className={styles.typeBadge} title={typeDescriptions[p.type] || ''}>
                    {typeLabels[p.type] || p.type}
                    <span className={styles.typeDescription}>{typeDescriptions[p.type] || ''}</span>
                  </span>
                )}
                {editing ? (
                  <input
                    type="text"
                    value={editData.demographics?.occupation || ''}
                    onChange={(e) => updateField('demographics.occupation', e.target.value)}
                    className={styles.editInputHeader}
                    placeholder="Beroep"
                    style={{ fontSize: '0.95rem', width: '200px' }}
                  />
                ) : (
                  p.demographics?.occupation && (
                    <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.95rem' }}>
                      {p.demographics.occupation}
                    </span>
                  )
                )}
              </div>
              {editing ? (
                <input
                  type="text"
                  value={editData.quote || ''}
                  onChange={(e) => updateField('quote', e.target.value)}
                  className={styles.editInputHeader}
                  placeholder="Typerende quote"
                  style={{ fontStyle: 'italic', fontSize: '0.95rem', width: '100%', marginTop: '0.5rem' }}
                />
              ) : (
                p.quote && <p className={styles.quoteBlock}>"{p.quote}"</p>
              )}
            </div>
            <div className={styles.headerActions}>
              {editing ? (
                <>
                  <button className={styles.saveBtn} onClick={saveChanges} disabled={saving}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    {saving ? 'Opslaan...' : 'Opslaan'}
                  </button>
                  <button className={styles.backBtn} onClick={cancelEditing}>
                    Annuleren
                  </button>
                </>
              ) : (
                <>
                  {canEdit && (
                    <button className={styles.editBtn} onClick={startEditing}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                      Bewerken
                    </button>
                  )}
                  <button className={styles.backBtn} onClick={onClose}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="19" y1="12" x2="5" y2="12" />
                      <polyline points="12 19 5 12 12 5" />
                    </svg>
                    Terug
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'overview' ? styles.active : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overzicht
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'behavior' ? styles.active : ''}`}
            onClick={() => setActiveTab('behavior')}
          >
            Gedragsanalyse
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'intervention' ? styles.active : ''}`}
            onClick={() => setActiveTab('intervention')}
          >
            Interventie
          </button>
        </div>

        {/* Tab Content */}
        <div className={styles.tabContent}>
          {activeTab === 'overview' && (
            <div>
              {/* Biography */}
              <div className={styles.section}>
                <h3>Biografie</h3>
                <EditableText
                  value={p.biography || ''}
                  onChange={(v) => updateField('biography', v)}
                  editing={editing}
                  multiline
                  placeholder="Biografie van deze persona..."
                  className={styles.biographyText}
                />
              </div>

              {/* Demographics */}
              <div className={styles.section}>
                <h3>Demografie</h3>
                <div className={styles.infoGrid}>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Leeftijd</span>
                    {editing ? (
                      <input
                        type="text"
                        value={
                          editData.demographics?.age
                            ? `${editData.demographics.age.min || ''}-${editData.demographics.age.max || ''}`
                            : editData.demographics?.ageRange || ''
                        }
                        onChange={(e) => {
                          const match = e.target.value.match(/(\d+)[^\d]+(\d+)/);
                          if (match) {
                            updateField('demographics.age', { min: parseInt(match[1]), max: parseInt(match[2]) });
                          }
                          updateField('demographics.ageRange', e.target.value);
                        }}
                        className={styles.editInput}
                        placeholder="Bijv. 35-45"
                      />
                    ) : (
                      <span className={styles.infoValue}>
                        {p.demographics?.age ? `${p.demographics.age.min}-${p.demographics.age.max} jaar` : p.demographics?.ageRange || <span className={styles.emptyText}>Niet ingevuld</span>}
                      </span>
                    )}
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Geslacht</span>
                    <EditableSelect
                      value={p.demographics?.gender || ''}
                      onChange={(v) => updateField('demographics.gender', v)}
                      editing={editing}
                      options={[
                        { value: 'male', label: 'Man' },
                        { value: 'female', label: 'Vrouw' },
                        { value: 'other', label: 'Anders' }
                      ]}
                      labels={{ male: 'Man', female: 'Vrouw', other: 'Anders' }}
                      className={styles.infoValue}
                    />
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Opleiding</span>
                    <EditableSelect
                      value={p.demographics?.education || ''}
                      onChange={(v) => updateField('demographics.education', v)}
                      editing={editing}
                      options={[
                        { value: 'primary', label: 'Basisonderwijs' },
                        { value: 'secondary', label: 'Voortgezet onderwijs' },
                        { value: 'vocational', label: 'MBO' },
                        { value: 'bachelor', label: 'HBO / Bachelor' },
                        { value: 'master', label: 'WO / Master' },
                        { value: 'doctorate', label: 'Promotie' }
                      ]}
                      labels={{ primary: 'Basisonderwijs', secondary: 'Voortgezet onderwijs', vocational: 'MBO', bachelor: 'HBO / Bachelor', master: 'WO / Master', doctorate: 'Promotie' }}
                      className={styles.infoValue}
                    />
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Beroep</span>
                    <EditableText
                      value={p.demographics?.occupation || ''}
                      onChange={(v) => updateField('demographics.occupation', v)}
                      editing={editing}
                      placeholder="Beroep"
                      className={styles.infoValue}
                    />
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Gezinssituatie</span>
                    <EditableSelect
                      value={p.demographics?.familyStatus || ''}
                      onChange={(v) => updateField('demographics.familyStatus', v)}
                      editing={editing}
                      options={[
                        { value: 'single', label: 'Alleenstaand' },
                        { value: 'married', label: 'Getrouwd' },
                        { value: 'divorced', label: 'Gescheiden' },
                        { value: 'widowed', label: 'Weduwe/Weduwnaar' },
                        { value: 'partnered', label: 'Samenwonend' }
                      ]}
                      labels={familyStatusLabels}
                      className={styles.infoValue}
                    />
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Locatie</span>
                    {editing ? (
                      <input
                        type="text"
                        value={typeof editData.demographics?.location === 'string' ? editData.demographics.location : (editData.demographics?.location as any)?.city || ''}
                        onChange={(v) => updateField('demographics.location', { city: v.target.value })}
                        className={styles.editInput}
                        placeholder="Stad"
                      />
                    ) : (
                      <span className={styles.infoValue}>{getLocationString() || <span className={styles.emptyText}>Niet ingevuld</span>}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Daily Routine */}
              <div className={styles.section}>
                <h3>Dagindeling</h3>
                {editing ? (
                  <div className={styles.editListContainer}>
                    {(editData.dailyRoutine || []).map((item, i) => (
                      <div key={i} className={styles.editListItem}>
                        <span className={styles.routineBullet}>{String(i + 1).padStart(2, '0')}</span>
                        <input
                          type="text"
                          value={item}
                          onChange={(e) => {
                            const updated = [...(editData.dailyRoutine || [])];
                            updated[i] = e.target.value;
                            updateField('dailyRoutine', updated);
                          }}
                          className={styles.editInput}
                          style={{ flex: 1 }}
                        />
                        <button
                          className={styles.removeItemBtn}
                          onClick={() => {
                            const updated = [...(editData.dailyRoutine || [])];
                            updated.splice(i, 1);
                            updateField('dailyRoutine', updated);
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </button>
                      </div>
                    ))}
                    <button
                      className={styles.addItemBtn}
                      onClick={() => updateField('dailyRoutine', [...(editData.dailyRoutine || []), ''])}
                    >
                      + Item toevoegen
                    </button>
                  </div>
                ) : (
                  p.dailyRoutine && p.dailyRoutine.length > 0 ? (
                    <ul className={styles.routineList}>
                      {p.dailyRoutine.map((item, i) => (
                        <li key={i} className={styles.routineItem}>
                          <span className={styles.routineBullet}>{String(i + 1).padStart(2, '0')}</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className={styles.emptyText}>Geen dagindeling beschikbaar</span>
                  )
                )}
              </div>

              {/* Goals & Frustrations */}
              <div className={styles.section}>
                <h3>Doelen & Frustraties</h3>
                <div className={styles.infoGrid}>
                  <div>
                    <span className={styles.infoLabel}>Doelen</span>
                    <EditableTags
                      values={p.psychographics?.goals || []}
                      onChange={(v) => updateField('psychographics.goals', v)}
                      editing={editing}
                      placeholder="Doel 1, Doel 2, ..."
                    />
                  </div>
                  <div>
                    <span className={styles.infoLabel}>Frustraties</span>
                    <EditableTags
                      values={p.psychographics?.frustrations || []}
                      onChange={(v) => updateField('psychographics.frustrations', v)}
                      editing={editing}
                      placeholder="Frustratie 1, Frustratie 2, ..."
                    />
                  </div>
                  <div>
                    <span className={styles.infoLabel}>Behoeften</span>
                    <EditableTags
                      values={p.needs || []}
                      onChange={(v) => updateField('needs', v)}
                      editing={editing}
                      placeholder="Behoefte 1, Behoefte 2, ..."
                    />
                  </div>
                  <div>
                    <span className={styles.infoLabel}>Pijnpunten</span>
                    <EditableTags
                      values={p.painPoints || []}
                      onChange={(v) => updateField('painPoints', v)}
                      editing={editing}
                      placeholder="Pijnpunt 1, Pijnpunt 2, ..."
                    />
                  </div>
                  <div>
                    <span className={styles.infoLabel}>Interesses</span>
                    <EditableTags
                      values={p.psychographics?.interests || []}
                      onChange={(v) => updateField('psychographics.interests', v)}
                      editing={editing}
                      placeholder="Interesse 1, Interesse 2, ..."
                    />
                  </div>
                  <div>
                    <span className={styles.infoLabel}>Waarden</span>
                    <EditableTags
                      values={p.psychographics?.values || []}
                      onChange={(v) => updateField('psychographics.values', v)}
                      editing={editing}
                      placeholder="Waarde 1, Waarde 2, ..."
                    />
                  </div>
                </div>
              </div>

              {/* Current vs Desired Behavior */}
              <div className={styles.section}>
                <h3>Huidig vs. Gewenst Gedrag</h3>
                <div className={styles.behaviorComparison}>
                  <div className={styles.behaviorBox}>
                    <h4>Huidig Gedrag</h4>
                    <EditableText
                      value={p.currentBehavior || ''}
                      onChange={(v) => updateField('currentBehavior', v)}
                      editing={editing}
                      multiline
                      placeholder="Beschrijf het huidige gedrag..."
                    />
                  </div>
                  <div className={styles.behaviorBox}>
                    <h4>Gewenst Gedrag</h4>
                    <EditableText
                      value={p.desiredBehavior || ''}
                      onChange={(v) => updateField('desiredBehavior', v)}
                      editing={editing}
                      multiline
                      placeholder="Beschrijf het gewenste gedrag..."
                    />
                  </div>
                </div>
              </div>

              {/* Communication & Channels */}
              <div className={styles.section}>
                <h3>Communicatie</h3>
                <div className={styles.infoGrid}>
                  <div>
                    <span className={styles.infoLabel}>
                      Mediakanalen
                      <span className={styles.labelHint}>Waar ontvangt deze persona informatie?</span>
                    </span>
                    <EditableTags
                      values={getMediaChannels()}
                      onChange={(v) => updateField('behaviors.mediaConsumption.preferredChannels', v)}
                      editing={editing}
                      placeholder="Bijv. lokale Facebookgroep, Nextdoor, huis-aan-huiskrant..."
                    />
                  </div>
                  <div>
                    <span className={styles.infoLabel}>
                      Communicatievoorkeuren
                      <span className={styles.labelHint}>Hoe wil deze persona benaderd worden?</span>
                    </span>
                    <EditableTags
                      values={p.communicationPreferences || []}
                      onChange={(v) => updateField('communicationPreferences', v)}
                      editing={editing}
                      placeholder="Bijv. persoonlijke brief, bewonersbijeenkomst, inloopspreekuur..."
                    />
                  </div>
                  <div>
                    <span className={styles.infoLabel}>Triggers</span>
                    <EditableTags
                      values={p.triggers || []}
                      onChange={(v) => updateField('triggers', v)}
                      editing={editing}
                      placeholder="Trigger 1, Trigger 2, ..."
                    />
                  </div>
                </div>
              </div>

              {/* Project Relation */}
              <div className={styles.section}>
                <h3>Projectrelatie</h3>
                <div className={styles.infoGrid}>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Bewustzijn</span>
                    <EditableSelect
                      value={p.projectRelation?.awareness || p.projectRelation?.awarenessLevel || ''}
                      onChange={(v) => updateField('projectRelation.awareness', v)}
                      editing={editing}
                      options={[
                        { value: 'unaware', label: 'Onbewust' },
                        { value: 'aware', label: 'Bewust' },
                        { value: 'interested', label: 'Geïnteresseerd' },
                        { value: 'engaged', label: 'Betrokken' },
                        { value: 'advocate', label: 'Ambassadeur' }
                      ]}
                      labels={awarenessLabels}
                      className={styles.infoValue}
                    />
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Sentiment</span>
                    <EditableSelect
                      value={p.projectRelation?.sentiment || ''}
                      onChange={(v) => updateField('projectRelation.sentiment', v)}
                      editing={editing}
                      options={[
                        { value: 'very_negative', label: 'Zeer negatief' },
                        { value: 'negative', label: 'Negatief' },
                        { value: 'neutral', label: 'Neutraal' },
                        { value: 'positive', label: 'Positief' },
                        { value: 'very_positive', label: 'Zeer positief' }
                      ]}
                      labels={sentimentLabels}
                      className={styles.infoValue}
                    />
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Invloed</span>
                    <EditableSelect
                      value={p.projectRelation?.influence || ''}
                      onChange={(v) => updateField('projectRelation.influence', v)}
                      editing={editing}
                      options={[
                        { value: 'low', label: 'Laag' },
                        { value: 'medium', label: 'Gemiddeld' },
                        { value: 'high', label: 'Hoog' },
                        { value: 'very_high', label: 'Zeer hoog' }
                      ]}
                      labels={influenceLabels}
                      className={styles.infoValue}
                    />
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Betrokkenheid</span>
                    <EditableSelect
                      value={p.projectRelation?.engagement || ''}
                      onChange={(v) => updateField('projectRelation.engagement', v)}
                      editing={editing}
                      options={[
                        { value: 'passive', label: 'Passief' },
                        { value: 'active', label: 'Actief' },
                        { value: 'very_active', label: 'Zeer actief' }
                      ]}
                      labels={engagementLabels}
                      className={styles.infoValue}
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className={styles.section}>
                <h3>Notities</h3>
                <EditableText
                  value={p.notes || ''}
                  onChange={(v) => updateField('notes', v)}
                  editing={editing}
                  multiline
                  placeholder="Aanvullende notities..."
                  className={styles.biographyText}
                />
              </div>
            </div>
          )}

          {activeTab === 'behavior' && (
            <div>
              {/* Behavioral Determinants */}
              {hasCASIData || editing ? (
                <>
                  <div className={styles.section}>
                    <h3>CASI Gedragsbepalers</h3>
                    <div className={styles.determinantGrid}>
                      {Object.entries(determinantLabels).map(([key, label]) => {
                        const det = p.behavioralDeterminants?.[key] || {};
                        if (!editing && !det.description && !det.effect && !det.leverage) return null;
                        const isEmotie = key === 'emoties';
                        return (
                          <div key={key} className={`${styles.determinantCard} ${isEmotie ? styles.determinantCardEmotie : ''}`}>
                            <h4 className={styles.determinantName}>
                              {isEmotie && (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#E6007E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6, verticalAlign: 'middle' }}>
                                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                                </svg>
                              )}
                              {label}
                              {isEmotie && <span className={styles.emotieHint}>Dominante emotie + bron</span>}
                            </h4>
                            <div className={styles.determinantField}>
                              <div className={styles.fieldLabel}>Beschrijving</div>
                              <EditableText
                                value={det.description || ''}
                                onChange={(v) => updateField(`behavioralDeterminants.${key}.description`, v)}
                                editing={editing}
                                multiline
                                placeholder="Beschrijving..."
                                className={styles.fieldValue}
                              />
                            </div>
                            <div className={styles.determinantField}>
                              <div className={styles.fieldLabel}>Effect</div>
                              <EditableText
                                value={det.effect || ''}
                                onChange={(v) => updateField(`behavioralDeterminants.${key}.effect`, v)}
                                editing={editing}
                                placeholder="Effect op gedrag..."
                                className={styles.fieldValue}
                              />
                            </div>
                            <div className={styles.determinantField}>
                              <div className={styles.fieldLabel}>Hefboom</div>
                              <EditableText
                                value={det.leverage || ''}
                                onChange={(v) => updateField(`behavioralDeterminants.${key}.leverage`, v)}
                                editing={editing}
                                placeholder="Hefboom voor verandering..."
                                className={styles.fieldValue}
                              />
                            </div>
                            {(editing || det.source) && (
                              <div className={styles.determinantField}>
                                <div className={styles.fieldLabel}>Bron</div>
                                <EditableText
                                  value={det.source || ''}
                                  onChange={(v) => updateField(`behavioralDeterminants.${key}.source`, v)}
                                  editing={editing}
                                  placeholder="Bron..."
                                  className={styles.fieldValue}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Top 3 Determinants */}
                  <div className={styles.section}>
                    <h3>Top 3 Gedragsbepalers</h3>
                    {editing ? (
                      <div className={styles.topThreeGrid}>
                        {[0, 1, 2].map(i => {
                          const det = editData.top3Determinants?.[i] || {};
                          return (
                            <div key={i} className={styles.topThreeCard}>
                              <span className={styles.topThreeRank}>{i + 1}</span>
                              <input
                                type="text"
                                value={det.name || ''}
                                onChange={(e) => {
                                  const updated = [...(editData.top3Determinants || [{}, {}, {}])];
                                  while (updated.length <= i) updated.push({});
                                  updated[i] = { ...updated[i], name: e.target.value };
                                  updateField('top3Determinants', updated);
                                }}
                                className={styles.editInput}
                                placeholder="Naam gedragsbepaler"
                                style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}
                              />
                              <span className={styles.topThreeLabel}>Redenering</span>
                              <textarea
                                value={det.reasoning || ''}
                                onChange={(e) => {
                                  const updated = [...(editData.top3Determinants || [{}, {}, {}])];
                                  while (updated.length <= i) updated.push({});
                                  updated[i] = { ...updated[i], reasoning: e.target.value };
                                  updateField('top3Determinants', updated);
                                }}
                                className={styles.editTextarea}
                                placeholder="Waarom is dit belangrijk?"
                                rows={2}
                              />
                              <span className={styles.topThreeLabel}>Communicatie-implicatie</span>
                              <textarea
                                value={det.communicationImplication || ''}
                                onChange={(e) => {
                                  const updated = [...(editData.top3Determinants || [{}, {}, {}])];
                                  while (updated.length <= i) updated.push({});
                                  updated[i] = { ...updated[i], communicationImplication: e.target.value };
                                  updateField('top3Determinants', updated);
                                }}
                                className={styles.editTextarea}
                                placeholder="Wat betekent dit voor communicatie?"
                                rows={2}
                              />
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      p.top3Determinants && p.top3Determinants.length > 0 ? (
                        <div className={styles.topThreeGrid}>
                          {p.top3Determinants.map((det, i) => (
                            <div key={i} className={styles.topThreeCard}>
                              <span className={styles.topThreeRank}>{i + 1}</span>
                              <h4>{det.name}</h4>
                              {det.reasoning && (
                                <>
                                  <span className={styles.topThreeLabel}>Redenering</span>
                                  <p>{det.reasoning}</p>
                                </>
                              )}
                              {det.communicationImplication && (
                                <>
                                  <span className={styles.topThreeLabel}>Communicatie-implicatie</span>
                                  <p>{det.communicationImplication}</p>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className={styles.emptyText}>Geen top 3 gedragsbepalers beschikbaar</span>
                      )
                    )}
                  </div>

                  {/* Moment of Truth */}
                  <div className={styles.section}>
                    <h3>Moment of Truth</h3>
                    <div className={styles.momentOfTruth}>
                      <EditableText
                        value={p.momentOfTruth || ''}
                        onChange={(v) => updateField('momentOfTruth', v)}
                        editing={editing}
                        multiline
                        placeholder="Het beslissende moment voor deze persona..."
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className={styles.section}>
                  <h3>Gedragsanalyse</h3>
                  <p className={styles.emptyText}>
                    Geen CASI-gedragsanalyse beschikbaar. Upload een projectdocument om de AI-analyse te starten.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'intervention' && (
            <div>
              {/* Intervention Checklist */}
              <div className={styles.section}>
                <h3>Interventie Toetslijst</h3>
                {editing ? (
                  <div className={styles.editListContainer}>
                    {(editData.interventionChecklist || []).map((item, i) => (
                      <div key={i} className={styles.editListItem}>
                        <span className={styles.checkIcon}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="9 11 12 14 22 4" />
                            <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
                          </svg>
                        </span>
                        <input
                          type="text"
                          value={item}
                          onChange={(e) => {
                            const updated = [...(editData.interventionChecklist || [])];
                            updated[i] = e.target.value;
                            updateField('interventionChecklist', updated);
                          }}
                          className={styles.editInput}
                          style={{ flex: 1 }}
                        />
                        <button
                          className={styles.removeItemBtn}
                          onClick={() => {
                            const updated = [...(editData.interventionChecklist || [])];
                            updated.splice(i, 1);
                            updateField('interventionChecklist', updated);
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </button>
                      </div>
                    ))}
                    <button
                      className={styles.addItemBtn}
                      onClick={() => updateField('interventionChecklist', [...(editData.interventionChecklist || []), ''])}
                    >
                      + Item toevoegen
                    </button>
                  </div>
                ) : (
                  p.interventionChecklist && p.interventionChecklist.length > 0 ? (
                    <ul className={styles.checklist}>
                      {p.interventionChecklist.map((item, i) => (
                        <li key={i} className={styles.checklistItem}>
                          <span className={styles.checkIcon}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="9 11 12 14 22 4" />
                              <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
                            </svg>
                          </span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className={styles.emptyText}>Geen interventie-items beschikbaar.</span>
                  )
                )}
              </div>

              {/* Quality Control */}
              <div className={styles.section}>
                <h3>Kwaliteitscontrole</h3>
                <div className={styles.qualityGrid}>
                  <div className={styles.qualityBox}>
                    <h4>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FF9800" strokeWidth="2">
                        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                        <line x1="12" y1="9" x2="12" y2="13" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                      </svg>
                      <span className={styles.warningColor}>Stereotype-risico's</span>
                    </h4>
                    {editing ? (
                      <div className={styles.editListContainer}>
                        {(editData.qualityControl?.stereotypeRisks || []).map((risk, i) => (
                          <div key={i} className={styles.editListItem}>
                            <input
                              type="text"
                              value={risk}
                              onChange={(e) => {
                                const updated = [...(editData.qualityControl?.stereotypeRisks || [])];
                                updated[i] = e.target.value;
                                updateField('qualityControl.stereotypeRisks', updated);
                              }}
                              className={styles.editInput}
                              style={{ flex: 1 }}
                            />
                            <button
                              className={styles.removeItemBtn}
                              onClick={() => {
                                const updated = [...(editData.qualityControl?.stereotypeRisks || [])];
                                updated.splice(i, 1);
                                updateField('qualityControl.stereotypeRisks', updated);
                              }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                              </svg>
                            </button>
                          </div>
                        ))}
                        <button
                          className={styles.addItemBtn}
                          onClick={() => updateField('qualityControl.stereotypeRisks', [...(editData.qualityControl?.stereotypeRisks || []), ''])}
                        >
                          + Risico toevoegen
                        </button>
                      </div>
                    ) : (
                      p.qualityControl?.stereotypeRisks && p.qualityControl.stereotypeRisks.length > 0 ? (
                        <ul className={styles.qualityList}>
                          {p.qualityControl.stereotypeRisks.map((risk, i) => (
                            <li key={i} className={styles.qualityListItem}>
                              <span style={{ color: '#FF9800' }}>!</span>
                              {risk}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <span className={styles.emptyText}>Geen risico's</span>
                      )
                    )}
                  </div>
                  <div className={styles.qualityBox}>
                    <h4>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#277CEA" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                      </svg>
                      <span className={styles.infoColor}>Actiegerichte Vragen</span>
                    </h4>
                    {editing ? (
                      <div className={styles.editListContainer}>
                        {(editData.qualityControl?.actionableQuestions || []).map((q, i) => (
                          <div key={i} className={styles.editListItem}>
                            <input
                              type="text"
                              value={q}
                              onChange={(e) => {
                                const updated = [...(editData.qualityControl?.actionableQuestions || [])];
                                updated[i] = e.target.value;
                                updateField('qualityControl.actionableQuestions', updated);
                              }}
                              className={styles.editInput}
                              style={{ flex: 1 }}
                            />
                            <button
                              className={styles.removeItemBtn}
                              onClick={() => {
                                const updated = [...(editData.qualityControl?.actionableQuestions || [])];
                                updated.splice(i, 1);
                                updateField('qualityControl.actionableQuestions', updated);
                              }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                              </svg>
                            </button>
                          </div>
                        ))}
                        <button
                          className={styles.addItemBtn}
                          onClick={() => updateField('qualityControl.actionableQuestions', [...(editData.qualityControl?.actionableQuestions || []), ''])}
                        >
                          + Vraag toevoegen
                        </button>
                      </div>
                    ) : (
                      p.qualityControl?.actionableQuestions && p.qualityControl.actionableQuestions.length > 0 ? (
                        <ul className={styles.qualityList}>
                          {p.qualityControl.actionableQuestions.map((question, i) => (
                            <li key={i} className={styles.qualityListItem}>
                              <span style={{ color: '#277CEA' }}>?</span>
                              {question}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <span className={styles.emptyText}>Geen vragen</span>
                      )
                    )}
                  </div>
                </div>
              </div>

              {/* Sources & Data Quality */}
              <div className={styles.section}>
                <h3>Bronnen & Datakwaliteit</h3>
                <div className={styles.sourcesGrid}>
                  <div className={styles.sourceBox}>
                    <h4>Projectbronnen</h4>
                    <EditableText
                      value={p.sourcesAndDataQuality?.projectSources || ''}
                      onChange={(v) => updateField('sourcesAndDataQuality.projectSources', v)}
                      editing={editing}
                      multiline
                      placeholder="Gebruikte projectbronnen..."
                    />
                  </div>
                  <div className={styles.sourceBox}>
                    <h4>Aannames</h4>
                    <EditableText
                      value={
                        editing
                          ? (p.sourcesAndDataQuality?.assumptions || '')
                          : (p.sourcesAndDataQuality?.assumptions || '')
                              .replace(/^aanname[:\s]*/gi, '')
                              .replace(/\.\s*aanname[:\s]*/gi, '. ')
                              .replace(/;\s*aanname[:\s]*/gi, '; ')
                              .replace(/\n\s*aanname[:\s]*/gi, '\n')
                      }
                      onChange={(v) => updateField('sourcesAndDataQuality.assumptions', v)}
                      editing={editing}
                      multiline
                      placeholder="Beschrijf als feitelijke stellingen..."
                    />
                  </div>
                  <div className={styles.sourceBox}>
                    <h4>Kennislacunes</h4>
                    <EditableText
                      value={p.sourcesAndDataQuality?.knowledgeGaps || ''}
                      onChange={(v) => updateField('sourcesAndDataQuality.knowledgeGaps', v)}
                      editing={editing}
                      multiline
                      placeholder="Ontbrekende kennis..."
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
  );
};
